import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Button, Modal, message, Alert, Divider, Space, Spin, Input, Row, Col, Badge, Empty } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, FileOutlined, FileTextOutlined, AudioOutlined } from '@ant-design/icons';
import { getSongsThunk, createSongThunk, updateSongThunk, deleteSongThunk } from '../Feature/SongsSlice';
import { getRecordingsThunk } from '../Feature/RecordingsSlice';
import { getCurrentSpaceThunk } from '../Feature/SpacesSlice';
import { getPartitionDownloadFormats } from '../Utils/songPartitions';
import '../Styles/ActionButtons.css';

export const SongListPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { spaceId } = useParams();

    const currentSpace = useSelector((state) => state.spaces.current);
    const currentUser = useSelector((state) => state.users.current);
    const songs = useSelector((state) => state.songs.songs);
    const recordings = useSelector((state) => state.recordings.recordings);
    const songsStatus = useSelector((state) => state.songs.status);
    const songsError = useSelector((state) => state.songs.error);

    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [selectedSong, setSelectedSong] = useState(null);
    const [newSongName, setNewSongName] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const isOwner = Boolean(currentUser?.id && currentSpace.data?.space_owner?.id === currentUser.id);

    useEffect(() => {
        if (!currentSpace.data || currentSpace.data.id !== spaceId) {
            dispatch(getCurrentSpaceThunk(spaceId));
        }
    }, [spaceId, dispatch, currentSpace.data]);

    useEffect(() => {
        if (spaceId) {
            dispatch(getSongsThunk(spaceId));
            dispatch(getRecordingsThunk(spaceId));
        }
    }, [dispatch, spaceId]);

    const getSongStats = (songId) => {
        const songRecordings = recordings.filter(r => r.song && r.song.id === songId);
        return {
            recordingCount: songRecordings.length,
        };
    };

    const handleCreateClick = () => {
        setNewSongName('');
        setCreateModalVisible(true);
    };

    const handleCreateConfirm = async () => {
        if (!newSongName.trim()) {
            message.error('Song name cannot be empty');
            return;
        }

        setIsLoading(true);
        try {
            await dispatch(createSongThunk({
                spaceId,
                data: { song_name: newSongName }
            })).unwrap();

            message.success('Song created successfully');
            setCreateModalVisible(false);
            setNewSongName('');
            dispatch(getSongsThunk(spaceId));
        } catch (error) {
            message.error('Failed to create song: ' + (error.message || 'Unknown error'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = (song) => {
        setSelectedSong(song);
        setNewSongName(song.song_name);
        setEditModalVisible(true);
    };

    const handleEditConfirm = async () => {
        if (!newSongName.trim()) {
            message.error('Song name cannot be empty');
            return;
        }

        setIsLoading(true);
        try {
            await dispatch(updateSongThunk({
                spaceId,
                songId: selectedSong.id,
                data: { song_name: newSongName }
            })).unwrap();

            message.success('Song updated successfully');
            setEditModalVisible(false);
            setSelectedSong(null);
            setNewSongName('');
            dispatch(getSongsThunk(spaceId));
        } catch (error) {
            message.error('Failed to update song: ' + (error.message || 'Unknown error'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (song) => {
        setSelectedSong(song);
        setDeleteModalVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedSong) return;

        setIsLoading(true);
        try {
            await dispatch(deleteSongThunk({
                spaceId,
                songId: selectedSong.id
            })).unwrap();

            message.success('Song deleted successfully');
            setDeleteModalVisible(false);
            setSelectedSong(null);
            dispatch(getSongsThunk(spaceId));
            dispatch(getRecordingsThunk(spaceId));
        } catch (error) {
            message.error('Failed to delete song: ' + (error.message || 'Unknown error'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSongClick = (song) => {
        navigate(`/spaces/${spaceId}/songs/${song.id}`);
    };

    if (songsStatus === 'failed') {
        return (
            <>
                <h1>Songs</h1>
                <Divider />
                <Alert
                    message="Unable to load songs"
                    description={songsError}
                    type="error"
                    showIcon
                />
            </>
        );
    }

    const stats = songs.reduce((acc, song) => {
        const songStats = getSongStats(song.id);
        return {
            ...acc,
            [song.id]: songStats
        };
    }, {});

    return (
        <>
            <div style={{ marginBottom: '30px' }}>
                <h1 style={{ marginBottom: '8px' }}>Songs</h1>
                <p style={{ color: '#666', margin: 0 }}>View songs, partitions, and lyrics</p>
            </div>

            {isOwner && <div style={{ marginBottom: '24px' }}>
                <Button
                    className="action-button-primary action-button-large"
                    icon={<PlusOutlined />}
                    onClick={handleCreateClick}
                >
                    Create New Song
                </Button>
            </div>}

            {songsStatus === 'loading' ? (
                <Spin size="large" style={{ display: 'flex', justifyContent: 'center', padding: '50px' }} />
            ) : songs.length === 0 ? (
                <Empty
                    description="No songs yet"
                    style={{ padding: '50px 0' }}
                />
            ) : (
                <Row gutter={[16, 16]}>
                    {songs.map((song) => {
                        const songStats = stats[song.id] || { recordingCount: 0 };
                        const partitionCount = (song.partitions || []).length;
                        const lyricsCount = (song.lyrics || []).length;
                        const exportablePartitionCount = (song.partitions || []).filter(
                            (partition) => getPartitionDownloadFormats(partition).length > 1
                        ).length;

                        return (
                            <Col key={song.id} xs={24} sm={12} lg={8}>
                                <Card
                                    hoverable
                                    onClick={() => handleSongClick(song)}
                                    style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    cover={
                                        <div style={{
                                            height: '100px',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            <AudioOutlined style={{ fontSize: '40px', color: '#fff' }} />
                                        </div>
                                    }
                                >
                                    <div style={{ flex: 1 }}>
                                        <h3 style={{ marginBottom: '12px', marginTop: 0 }}>
                                            {song.song_name}
                                        </h3>

                                        <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                            <Badge
                                                count={songStats.recordingCount}
                                                style={{ backgroundColor: '#1890ff' }}
                                                title="Recordings"
                                                overflowCount={999}
                                            />
                                            <Badge
                                                count={partitionCount}
                                                style={{ backgroundColor: '#52c41a' }}
                                                title="Partitions"
                                                overflowCount={999}
                                            />
                                            <Badge
                                                count={lyricsCount}
                                                style={{ backgroundColor: '#faad14' }}
                                                title="Lyrics"
                                                overflowCount={999}
                                            />
                                        </div>

                                        {exportablePartitionCount > 0 && (
                                            <p style={{ color: '#389e0d', marginBottom: '16px' }}>
                                                {exportablePartitionCount} partition{exportablePartitionCount > 1 ? 's' : ''} can be downloaded as PDF/GP
                                            </p>
                                        )}

                                        {isOwner && <div style={{
                                            display: 'flex',
                                            gap: '8px',
                                            paddingTop: '12px',
                                            borderTop: '1px solid #f0f0f0',
                                        }}>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditClick(song);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                type="text"
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClick(song);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </div>}
                                    </div>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            <Modal
                title="Create New Song"
                open={createModalVisible}
                onCancel={() => {
                    setCreateModalVisible(false);
                    setNewSongName('');
                }}
                onOk={handleCreateConfirm}
                confirmLoading={isLoading}
            >
                <Input
                    placeholder="Enter song name"
                    value={newSongName}
                    onChange={(e) => setNewSongName(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleCreateConfirm();
                        }
                    }}
                />
            </Modal>

            <Modal
                title={`Edit Song: "${selectedSong?.song_name}"`}
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    setSelectedSong(null);
                    setNewSongName('');
                }}
                onOk={handleEditConfirm}
                confirmLoading={isLoading}
            >
                <Input
                    placeholder="Enter song name"
                    value={newSongName}
                    onChange={(e) => setNewSongName(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleEditConfirm();
                        }
                    }}
                />
            </Modal>

            <Modal
                title={`Delete Song: "${selectedSong?.song_name}"`}
                open={deleteModalVisible}
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setSelectedSong(null);
                }}
                onOk={handleDeleteConfirm}
                confirmLoading={isLoading}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
            >
                <p>Are you sure you want to delete this song?</p>
                {selectedSong && getSongStats(selectedSong.id).recordingCount > 0 && (
                    <p style={{ color: '#ff4d4f' }}>
                        This song is used by <strong>{getSongStats(selectedSong.id).recordingCount}</strong> recording(s). These recordings will be unlinked.
                    </p>
                )}
            </Modal>
        </>
    );
};
